import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Brain, Layers, Image as ImageIcon, Database, BookOpen, ExternalLink } from 'lucide-react';
import { Button } from '../../../../../components/ui/Button';
import { Badge } from '../../../../../components/ui/Badge';
import { IssueRow, MODULE_CONFIG } from '../shared';
import type { UnifiedQuestionRow } from '../hooks/useGovernanceData';
interface QuestionDetailPanelProps {
  row: UnifiedQuestionRow;
  onOpenQuestionInBank?: (row: UnifiedQuestionRow) => void;
}
export function QuestionDetailPanel({
  row,
  onOpenQuestionInBank
}: QuestionDetailPanelProps) {
  const modConfig = MODULE_CONFIG[row.moduleCode] || {
    bgClass: 'bg-gray-500',
    textClass: 'text-gray-700'
  };
  return <div className="px-4 py-4 bg-gray-50 border-t border-gray-100" data-id="element-5493" data-mp="12ug71hjafd3q">
      <div className="mb-4 flex justify-end" data-id="element-5494" data-mp="29btkjza5s3mu">
        <Button variant="secondary" size="sm" onClick={() => onOpenQuestionInBank?.(row)} disabled={!onOpenQuestionInBank} data-id="element-5495" data-mp="29ezdjplwrpnu">
          <ExternalLink className="w-4 h-4 mr-1.5" data-id="element-5496" data-mp="2vljucomj36mm" />
          Open in Vragenbank
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5497" data-mp="2ql5kd11d9mly">
        {/* Left Column: Pipeline Issues */}
        <div className="space-y-4" data-id="element-5498" data-mp="2lpynod6ili6j">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-5499" data-mp="2gpu58hm1qddo">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-5500" data-mp="2coinszdbsygk" /> Pipeline Issues
          </h4>

          {row.pipelineIssues.length === 0 ? <div className="p-4 bg-white rounded-lg border border-gray-200 text-center" data-id="element-5501" data-mp="27vft2nwtbiys">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" data-id="element-5502" data-mp="22y5xsrmqsd31" />
              <p className="text-sm text-gray-600" data-id="element-5503" data-mp="1xzmaonwewv93">
                Geen pipeline issues gevonden.
              </p>
            </div> : <div className="space-y-2" data-id="element-5504" data-mp="1oxv2nceegcc2">
              {row.pipelineIssues.map((issue, idx) => <IssueRow key={idx} issue={issue} data-id="element-5505" data-mp="1ecrdao5arzc6" data-mpi={idx} />)}
            </div>}
        </div>

        {/* Right Column: Metadata & Quality */}
        <div className="space-y-6" data-id="element-5506" data-mp="1jdfeu3r5jykn">
          {/* Quality & Metadata */}
          <div data-id="element-5507" data-mp="15cuwf8p5g4ro">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5508" data-mp="15ded6m32cn44">
              <Brain className="w-3.5 h-3.5" data-id="element-5509" data-mp="1abfxfac86sh6" /> Kwaliteit & Metadata
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4" data-id="element-5510" data-mp="1y21vq9wfgydz">
              <div className="grid grid-cols-2 gap-4" data-id="element-5511" data-mp="221alz4s9ee4s">
                <div data-id="element-5512" data-mp="1oayqcvwgtx8w">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5513" data-mp="1obgu0dlgjdqu">Cognitief Niveau</p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-5514" data-mp="1ta1v0f8ja1gc">
                    Niveau {row.cognitiveLevel}
                  </p>
                </div>
                <div data-id="element-5515" data-mp="2gy1kzcy0tntv">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5516" data-mp="2gxrwjtsmjoqa">Module & Tier</p>
                  <div className="flex items-center gap-1.5" data-id="element-5517" data-mp="2lrnr0m9oitgp">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${modConfig.bgClass} text-white`} data-id="element-5518" data-mp="2v4d0z7hp3c9v">
                      {row.moduleCode}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-5519" data-mp="34tmxczjknh3s">
                      Tier {row.tier}
                    </span>
                  </div>
                </div>
                <div data-id="element-5520" data-mp="2c7butjcen64c">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5521" data-mp="2c6smmuck3w3i">Vraagtype</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-5522" data-mp="285gryaetskog">
                    {row.questionType}
                  </p>
                </div>
                <div data-id="element-5523" data-mp="2h4c0vu9988vg">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-5524" data-mp="2h3skwj35zuk2">Skill</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-5525" data-mp="1dg29hf82s6m">
                    {row.skill}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Seed Status */}
          <div data-id="element-5526" data-mp="2729o74xboqgm">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5527" data-mp="271q7frjes846">
              <Database className="w-3.5 h-3.5" data-id="element-5528" data-mp="2c0tcvd3awgco" /> Seed Status
            </h4>

            {row.seedWarnings.length === 0 ? <div className="p-3 bg-white rounded-lg border border-gray-200 flex items-center gap-2" data-id="element-5529" data-mp="2g13i2nj9r2qh">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-5530" data-mp="2l06ni935vayz" />
                <span className="text-sm text-gray-600" data-id="element-5531" data-mp="2q02saajon6fl">
                  In sync met taxonomy
                </span>
              </div> : <div className="space-y-2" data-id="element-5532" data-mp="2zqxh9mobck4q">
                {row.seedWarnings.map((warn, i) => <div key={i} className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800 font-mono" data-id="element-5533" data-mp="33t27ebfjyymm" data-mpi={i}>
                    {warn}
                  </div>)}
              </div>}
          </div>

          {/* Visual Status */}
          <div data-id="element-5534" data-mp="s4nlr21gz04r">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5535" data-mp="s3vs3wrfnch4">
              <ImageIcon className="w-3.5 h-3.5" data-id="element-5536" data-mp="o5y1cpi0kibm" /> Visuele Hulpmiddelen
            </h4>

            <div className="p-3 bg-white rounded-lg border border-gray-200" data-id="element-5537" data-mp="j6lcjz7l116e">
              {row.hasVisualProblems ? <div className="flex items-start gap-2 text-red-700" data-id="element-5538" data-mp="1fwug5vnmwx1j">
                  <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5539" data-mp="1bu7s85u33w9m" />
                  <span className="text-sm" data-id="element-5540" data-mp="171o3qdtuzgq1">
                    Er zijn problemen met de visuals (zie pipeline issues).
                  </span>
                </div> : <div className="flex items-start gap-2 text-emerald-700" data-id="element-5541" data-mp="1zj8tn94i275e">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-5542" data-mp="1usac667rz30k" />
                  <span className="text-sm" data-id="element-5543" data-mp="1uq7agox9flg0">
                    Visuals zijn geldig en correct geconfigureerd.
                  </span>
                </div>}
            </div>
          </div>

          {/* Pedagogisch Contract */}
          <div data-id="element-5544" data-mp="24wqmyl6edtxi">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-5545" data-mp="24v5xh9yxx16i">
              <BookOpen className="w-3.5 h-3.5" data-id="element-5546" data-mp="28w9brs1cap5m" /> Pedagogisch Contract
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3" data-id="element-5547" data-mp="1ux9nilhqoc9z">
              {(() => {
              const pedagogyIssues = row.pipelineIssues.filter(i => i.domain === 'pedagogy');
              const hasPedagogyBlockers = pedagogyIssues.some(i => i.kind === 'blocker');
              const hasPedagogyCoaches = pedagogyIssues.some(i => i.kind === 'coach');
              return <>
                    <div className="grid grid-cols-2 gap-3" data-id="element-5548" data-mp="18pckiriy6dxm">
                      <div data-id="element-5549" data-mp="18pvsbfevvlxx">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5550" data-mp="14o0skfclpgm9">DOK Niveau</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-5551" data-mp="14lxst3lnc94c">
                          {row.pipelineIssues.some(i => i.code === 'SEED_DOK_MISSING') ? <span className="text-amber-600" data-id="element-5552" data-mp="1sdcx31wzh0i8">
                              Niet ingesteld
                            </span> : 'Ingesteld'}
                        </p>
                      </div>
                      <div data-id="element-5553" data-mp="1wf6t2ww8hgc2">
                        <p className="text-xs text-gray-500 mb-1" data-id="element-5554" data-mp="1weyff2sae6zt">
                          Misconceptie Tags
                        </p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-5555" data-mp="70ih2wrk20ek">
                          {pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_UNTAGGED') ? <span className="text-amber-600" data-id="element-5556" data-mp="3hc8ug7u27aq5">Ontbreekt</span> : pedagogyIssues.some(i => i.code === 'PEDAGOGY_DISTRACTORS_PARTIAL') ? <span className="text-amber-500" data-id="element-5557" data-mp="3r0z4m6z96508">Gedeeltelijk</span> : <span className="text-emerald-600" data-id="element-5558" data-mp="2udue5xen8f7w">Compleet</span>}
                        </p>
                      </div>
                    </div>
                    {pedagogyIssues.length === 0 ? <div className="flex items-center gap-2 pt-1" data-id="element-5559" data-mp="38bj078kg5sej">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-5560" data-mp="389pqfkejes2b" />
                        <span className="text-sm text-gray-600" data-id="element-5561" data-mp="ilyv8u7sll1u">
                          Pedagogisch contract voldoet aan alle eisen.
                        </span>
                      </div> : <div className="flex items-center gap-2 pt-1" data-id="element-5562" data-mp="285oz2drjzfle">
                        {hasPedagogyBlockers ? <XCircle className="w-4 h-4 text-red-500" data-id="element-5563" data-mp="1ydt0z2pgvph6" /> : <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-5564" data-mp="1tepsf8aa314g" />}
                        <span className="text-sm text-gray-600" data-id="element-5565" data-mp="1pd4invt1j2yh">
                          {pedagogyIssues.length} pedagogische{' '}
                          {pedagogyIssues.length === 1 ? 'bevinding' : 'bevindingen'}
                          {hasPedagogyBlockers ? ' (blocker)' : hasPedagogyCoaches ? ' (verbeterpunt)' : ''}
                        </span>
                      </div>}
                  </>;
            })()}
            </div>
          </div>
        </div>
      </div>
    </div>;
}